/* Rico Perez cart rewards: progress toward a free gift and free shipping.
   Drives the full bar on the cart page and the slim bar in the header.
   Reward math lives in createLogic() so it can be tested without a browser. */
(function (global) {
  'use strict';

  function createLogic(cfg) {
    // A "tagged" line is one the picker added; a "free" line is one Shopify's
    // gift discount actually made $0. They are usually the same line, but when a
    // shopper also buys the same product, Shopify may free the other copy.
    function isTagged(item) {
      if (item.properties && item.properties._rp_gift) return true;
      return !!(cfg.giftProductId && item.product_id === cfg.giftProductId);
    }
    function isFree(item) {
      return item.final_line_price === 0 && item.original_line_price > 0;
    }
    function isGiftLine(item) {
      return isTagged(item) || isFree(item);
    }

    function eligibleCents(cart) {
      // Gift lines never count toward the next reward.
      return (cart.items || []).reduce(function (sum, item) {
        return isGiftLine(item) ? sum : sum + item.final_line_price;
      }, 0);
    }

    function giftsEarned(eligible) {
      if (!cfg.giftCents) return 0;
      var n = cfg.repeat ? Math.floor(eligible / cfg.giftCents) : eligible >= cfg.giftCents ? 1 : 0;
      return Math.min(n, cfg.maxGifts);
    }

    function units(lines) {
      return lines.reduce(function (sum, item) {
        return sum + item.quantity;
      }, 0);
    }

    // Lines to remove when a gift must go: the tagged ones first, else the free ones.
    function giftLines(cart) {
      var items = cart.items || [];
      var tagged = items.filter(isTagged);
      return tagged.length ? tagged : items.filter(isFree);
    }

    // Gifts the shopper actually has: whichever is higher, tagged units or free units.
    function giftsInCart(cart) {
      var items = cart.items || [];
      return Math.max(units(items.filter(isTagged)), units(items.filter(isFree)));
    }

    // Once free shipping is unlocked, the bar becomes an endless loop:
    // it fills toward the next gift, then starts over after every gift.
    function inCycle(eligible) {
      return !!(cfg.repeat && cfg.giftCents && eligible >= cfg.shipCents && giftsEarned(eligible) < cfg.maxGifts);
    }

    function cyclePct(eligible) {
      return ((eligible % cfg.giftCents) / cfg.giftCents) * 100;
    }

    function scaleMax() {
      return Math.max(cfg.giftCents, cfg.shipCents, 1);
    }

    // The nearest reward the customer has not reached yet.
    function nextStep(eligible) {
      var gifts = giftsEarned(eligible);
      var candidates = [];
      var nextGiftAt = gifts < cfg.maxGifts && (cfg.repeat || gifts === 0) ? (gifts + 1) * cfg.giftCents : 0;
      if (nextGiftAt && nextGiftAt > eligible) candidates.push({ type: 'gift', left: nextGiftAt - eligible, again: gifts > 0 });
      if (eligible < cfg.shipCents) candidates.push({ type: 'ship', left: cfg.shipCents - eligible });
      if (!candidates.length) return { type: 'done', left: 0 };
      candidates.sort(function (a, b) {
        return a.left - b.left;
      });
      return candidates[0];
    }

    return {
      eligibleCents: eligibleCents,
      giftsEarned: giftsEarned,
      giftLines: giftLines,
      giftsInCart: giftsInCart,
      scaleMax: scaleMax,
      inCycle: inCycle,
      cyclePct: cyclePct,
      nextStep: nextStep
    };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { createLogic: createLogic };
  if (typeof document === 'undefined') return;
  if (global.__rpRewardsReady) return;

  var roots = document.querySelectorAll('[data-rewards]');
  if (!roots.length) return;
  global.__rpRewardsReady = true;

  var designMode = !!(global.Shopify && global.Shopify.designMode);
  var updaters = [];

  function money(cents, currency) {
    var whole = cents % 100 === 0;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(whole ? 0 : 2);
    }
  }

  function setTemplate(node, template, amount) {
    node.textContent = '';
    String(template || '')
      .split('{amount}')
      .forEach(function (part, i) {
        if (i > 0) {
          var strong = document.createElement('strong');
          strong.textContent = amount;
          node.appendChild(strong);
        }
        node.appendChild(document.createTextNode(part));
      });
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error('cart request failed');
      return res.json();
    });
  }

  function addUrl() {
    return (global.routes && global.routes.cart_add_url ? global.routes.cart_add_url : '/cart/add') + '.js';
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (res) {
      return res.json();
    });
  }

  function reduceGifts(lines, remaining) {
    if (!remaining || !lines.length) return Promise.resolve();
    var line = lines[0];
    var cut = Math.min(line.quantity, remaining);
    return post('/cart/change.js', { id: line.key, quantity: line.quantity - cut }).then(function () {
      return reduceGifts(lines.slice(1), remaining - cut);
    });
  }

  // Refresh the cart page in place (items, totals, header bubble) after the
  // gift line changes, then tell every listener (rewards bar, savings box).
  function reloadSoon(sync, noReload) {
    var items = document.getElementById('main-cart-items');
    var footer = document.getElementById('main-cart-footer');
    if (!items || !footer || !items.dataset.id || !footer.dataset.id) {
      global.location.reload();
      return Promise.resolve();
    }
    var ids = [items.dataset.id, footer.dataset.id, 'cart-icon-bubble'];
    return fetch('/?sections=' + ids.join(',') + '&t=' + Date.now(), { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) {
        return r.json();
      })
      .then(function (secs) {
        function swap(el, id, sel) {
          if (!secs[id]) return;
          var doc = new DOMParser().parseFromString(secs[id], 'text/html');
          var from = sel ? doc.querySelector(sel) : doc.body.firstElementChild;
          var to = sel ? el.querySelector(sel) : el;
          if (from && to) to.innerHTML = from.innerHTML;
        }
        swap(items, items.dataset.id, '.js-contents');
        swap(footer, footer.dataset.id, '.js-contents');
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble) swap(bubble, 'cart-icon-bubble', '.shopify-section');
        return fetchCart();
      })
      .then(function (cart) {
        var empty = !cart.item_count;
        items.classList.toggle('is-empty', empty);
        footer.classList.toggle('is-empty', empty);
        updaters.forEach(function (fn) {
          fn(cart, !sync);
        });
      })
      .catch(function () {
        global.location.reload();
      });
  }

  function initRoot(root) {
    var d = root.dataset;
    var mini = d.mode === 'mini';
    var cfg = {
      giftCents: parseInt(d.giftCents, 10) || 0,
      shipCents: parseInt(d.shipCents, 10) || 0,
      repeat: d.repeat === 'true',
      maxGifts: parseInt(d.maxGifts, 10) || 1,
      autoAdd: !mini && d.autoAdd === 'true',
      giftProductId: parseInt(d.giftProductId, 10) || 0,
      giftVariantId: parseInt(d.giftVariantId, 10) || 0,
      giftAvailable: d.giftAvailable === 'true'
    };
    var msgs = { first: d.msgGiftFirst, next: d.msgGiftNext, ship: d.msgShip, done: d.msgDone, more: d.msgMore || d.msgGiftNext };
    var logic = createLogic(cfg);
    var pool = d.pool === 'true';
    var q = function (sel) {
      return root.querySelector(sel);
    };
    var els = {
      msg: q('[data-rewards-msg]'),
      bar: q('[data-rewards-bar]'),
      rest: q('[data-rewards-rest]'),
      gift: q('[data-marker="gift"]'),
      ship: q('[data-marker="ship"]'),
      giftName: q('[data-marker="gift"] [data-name]'),
      giftAmt: q('[data-marker="gift"] [data-amt]'),
      row: q('[data-rewards-row]'),
      rowCount: q('[data-rewards-count]'),
      addBtn: q('[data-rewards-add]')
    };

    function place(marker, cents, scale) {
      var pos = (cents / scale) * 100;
      marker.style.left = pos + '%';
      marker.classList.toggle('is-end', pos >= 88);
    }

    function render(cart) {
      var empty = !cart || !cart.items || !cart.item_count;
      if (empty && !(mini && d.showEmpty === 'true')) {
        root.hidden = true;
        return;
      }
      if (empty) cart = { items: [], item_count: 0, currency: cart && cart.currency };
      root.hidden = false;
      root.classList.remove('rp-rewards--pending');

      var eligible = logic.eligibleCents(cart);
      var gifts = logic.giftsEarned(eligible);
      var step = logic.nextStep(eligible);
      var cycle = logic.inCycle(eligible);
      var scale = cycle ? cfg.giftCents : logic.scaleMax();
      var pct = cycle ? logic.cyclePct(eligible) : Math.min(eligible / scale, 1) * 100;

      els.rest.style.width = 100 - pct + '%';
      els.bar.setAttribute('aria-valuenow', String(Math.round(pct)));

      place(els.gift, cycle ? scale : cfg.giftCents, scale);
      place(els.ship, cfg.shipCents, scale);
      els.ship.hidden = cycle;
      els.gift.classList.toggle('is-reached', !cycle && gifts > 0);
      els.ship.classList.toggle('is-reached', eligible >= cfg.shipCents);
      if (els.giftName) {
        els.giftName.textContent = cycle
          ? els.giftName.dataset.base + ' #' + (gifts + 1)
          : els.giftName.dataset.base + (gifts > 1 ? ' ×' + gifts : '');
      }
      if (els.giftAmt) els.giftAmt.textContent = money(cycle ? (gifts + 1) * cfg.giftCents : cfg.giftCents, cart.currency);

      if (cycle) setTemplate(els.msg, msgs.more.replace('{count}', String(gifts)), money(step.left, cart.currency));
      else if (step.type === 'gift') setTemplate(els.msg, step.again ? msgs.next : msgs.first, money(step.left, cart.currency));
      else if (step.type === 'ship') setTemplate(els.msg, msgs.ship, money(step.left, cart.currency));
      else setTemplate(els.msg, msgs.done, '');

      if (els.row && !pool) {
        var have = logic.giftsInCart(cart);
        els.row.hidden = !(cfg.giftProductId && gifts > 0);
        els.rowCount.textContent = gifts > 1 ? ' ×' + gifts : '';
        els.addBtn.hidden = cfg.autoAdd || have >= gifts || !cfg.giftAvailable;
      }
    }

    // Make the gift quantity in the cart match what the customer has earned.
    function syncGift(cart, guard) {
      if (!cfg.giftProductId || !cfg.giftVariantId || !cfg.giftAvailable) return;
      var want = logic.giftsEarned(logic.eligibleCents(cart));
      var have = logic.giftsInCart(cart);
      if (want === have) return;
      var job =
        want > have
          ? post(addUrl(), { id: cfg.giftVariantId, quantity: want - have })
          : reduceGifts(logic.giftLines(cart), have - want);
      job
        .then(function () {
          reloadSoon();
        })
        .catch(function () {});
    }

    // ---- Gift picker (collection mode) ----
    var picker = null;
    if (pool) {
      var poolData = [];
      try {
        poolData = JSON.parse(root.querySelector('[data-pool-json]').textContent).filter(Boolean);
      } catch (e) {}
      var modal = root.querySelector('[data-pool-modal]');
      document.body.appendChild(modal);
      var grid = modal.querySelector('[data-pool-grid]');
      var subEl = modal.querySelector('[data-pool-sub]');
      var textEl = q('[data-pool-text]');
      var chooseBtn = q('[data-pool-choose]');
      var changeBtn = q('[data-pool-change]');
      var lastCart = null;
      var busy = false;

      var missing = function () {
        if (!lastCart) return 0;
        return Math.max(0, logic.giftsEarned(logic.eligibleCents(lastCart)) - logic.giftsInCart(lastCart));
      };
      var closeModal = function () {
        modal.hidden = true;
        document.documentElement.style.overflow = '';
      };
      var openModal = function () {
        subEl.textContent = missing() > 1 ? 'You have ' + missing() + ' free gifts to choose.' : 'Pick one on us.';
        modal.hidden = false;
        document.documentElement.style.overflow = 'hidden';
      };
      var addGift = function (variantId, btn) {
        if (busy) return;
        busy = true;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        post(addUrl(), { id: variantId, quantity: 1, properties: { _rp_gift: '1' } })
          .then(function () {
            return reloadSoon(false, true);
          })
          .then(function () {
            busy = false;
            if (missing() > 0) {
              subEl.textContent = missing() > 1 ? 'You have ' + missing() + ' free gifts to choose.' : 'Pick one on us.';
              buildGrid();
            } else closeModal();
          })
          .catch(function () {
            busy = false;
            btn.disabled = false;
            btn.textContent = 'Try again';
          });
      };
      var buildGrid = function () {
        grid.textContent = '';
        poolData.forEach(function (p) {
          var card = document.createElement('div');
          card.className = 'rp-gp__item';
          if (p.img) {
            var im = document.createElement('img');
            im.src = p.img;
            im.alt = '';
            im.loading = 'lazy';
            card.appendChild(im);
          }
          var nm = document.createElement('div');
          nm.className = 'rp-gp__name';
          nm.textContent = p.title;
          card.appendChild(nm);
          var pr = document.createElement('div');
          pr.className = 'rp-gp__price';
          var old = document.createElement('s');
          old.textContent = p.price;
          var fr = document.createElement('b');
          fr.textContent = 'FREE';
          pr.appendChild(old);
          pr.appendChild(fr);
          card.appendChild(pr);
          var sel = null;
          if (p.v.length > 1) {
            sel = document.createElement('select');
            p.v.forEach(function (v) {
              var o = document.createElement('option');
              o.value = v.id;
              o.textContent = v.t;
              sel.appendChild(o);
            });
            card.appendChild(sel);
          }
          var b = document.createElement('button');
          b.type = 'button';
          b.textContent = 'Choose';
          b.addEventListener('click', function () {
            addGift(sel ? sel.value : p.v[0] && p.v[0].id, b);
          });
          if (!p.v.length) b.disabled = true;
          card.appendChild(b);
          grid.appendChild(card);
        });
      };
      Array.prototype.forEach.call(modal.querySelectorAll('[data-pool-close]'), function (el) {
        el.addEventListener('click', closeModal);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !modal.hidden) closeModal();
      });
      if (chooseBtn)
        chooseBtn.addEventListener('click', function () {
          buildGrid();
          openModal();
        });
      if (changeBtn)
        changeBtn.addEventListener('click', function () {
          if (!lastCart) return;
          changeBtn.disabled = true;
          reduceGifts(logic.giftLines(lastCart), logic.giftsInCart(lastCart))
            .then(function () {
              return reloadSoon(false, true);
            })
            .then(function () {
              changeBtn.disabled = false;
              buildGrid();
              openModal();
            })
            .catch(function () {
              changeBtn.disabled = false;
            });
        });

      picker = function (cart, noSync) {
        lastCart = cart;
        var want = logic.giftsEarned(logic.eligibleCents(cart));
        var have = logic.giftsInCart(cart);
        var open = want > have;
        if (els.row) els.row.hidden = !(want > 0 || have > 0);
        if (textEl) {
          textEl.textContent = open
            ? 'You earned ' + (want - have > 1 ? want - have + ' free gifts' : 'a free gift') + ' — choose yours!'
            : 'Free gift added to your cart ✓';
        }
        if (chooseBtn) chooseBtn.hidden = !open || !poolData.length;
        if (changeBtn) changeBtn.hidden = open || have < 1 || !poolData.length;
        // too many gifts for what is earned: remove extras
        if (have > want && !designMode && !noSync) {
          reduceGifts(logic.giftLines(cart), have - want).then(function () {
            reloadSoon(false);
          });
          return;
        }
        if (open && d.autoPrompt === 'true' && !designMode && poolData.length && modal.hidden) {
          var key = '';
          try {
            key = sessionStorage.getItem('rp-gift-prompted') || '';
          } catch (e) {}
          var tag = want + ':' + have;
          if (key !== tag) {
            try {
              sessionStorage.setItem('rp-gift-prompted', tag);
            } catch (e) {}
            buildGrid();
            openModal();
          }
        }
        if (!open) {
          try {
            sessionStorage.removeItem('rp-gift-prompted');
          } catch (e) {}
        }
      };
    }

    function afterCart(cart, noSync) {
      render(cart);
      if (picker) {
        picker(cart, noSync);
        return;
      }
      if (!noSync && cfg.autoAdd && !designMode) syncGift(cart, false);
    }
    updaters.push(afterCart);

    if (els.addBtn) {
      els.addBtn.addEventListener('click', function () {
        els.addBtn.disabled = true;
        fetchCart()
          .then(function (cart) {
            var want = logic.giftsEarned(logic.eligibleCents(cart));
            var have = logic.giftsInCart(cart);
            if (want <= have) return;
            return post(addUrl(), { id: cfg.giftVariantId, quantity: want - have });
          })
          .then(function () {
            reloadSoon();
          })
          .catch(function () {
            els.addBtn.disabled = false;
          });
      });
    }

    // First paint from the cart JSON the server put on the page.
    var initial = null;
    try {
      initial = JSON.parse(document.getElementById(d.cartJson).textContent);
    } catch (e) {}
    if (initial) {
      render(initial);
      if (picker) picker(initial, false);
      else if (cfg.autoAdd && !designMode) syncGift(initial, true);
    } else {
      fetchCart().then(afterCart);
    }
  }

  global.rpRefreshCart = function () {
    reloadSoon(true);
  };

  Array.prototype.forEach.call(roots, initRoot);

  // Header cart badge (the little number on the bag icon).
  function updateBadge(cart) {
    var btn = document.getElementById('cart-icon-bubble');
    if (!btn || !cart) return;
    var n = cart.item_count || 0;
    var badge = btn.querySelector('.rp-count');
    if (!n) {
      if (badge) badge.remove();
    } else {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'rp-count';
        badge.setAttribute('aria-hidden', 'true');
        btn.appendChild(badge);
      }
      badge.textContent = n;
    }
    btn.setAttribute('aria-label', 'Cart (' + n + ')');
  }

  function runAll(cart) {
    updateBadge(cart);
    updaters.forEach(function (fn) {
      fn(cart);
    });
  }

  function onCartChanged(cart) {
    if (cart && cart.items) runAll(cart);
    else fetchCart().then(runAll);
  }

  // Dawn announces every quantity change or removal on the cart page.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      onCartChanged(event && event.cartData);
    });
  }

  // Any of our own sections (Shop quick add, suggestions) can announce a change.
  document.addEventListener('rp:cart-updated', function (e) {
    onCartChanged(e && e.detail);
  });
})(typeof window !== 'undefined' ? window : this);
